import { AlertTriangle, Bug,Home, RefreshCw } from 'lucide-react';
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Atualiza o state para mostrar a UI de fallback
    return { 
      hasError: true, 
      error,
      errorInfo: null 
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary capturou um erro:', error, errorInfo);
    
    // ✅ Verificação específica para erros de contexto do React Router
    if (error.message.includes('useContext') || error.message.includes('useParams')) {
      console.error('🚨 Erro de contexto do React Router detectado');
      console.error('Contexto não disponível:', errorInfo);
    }
    
    this.setState({
      error,
      errorInfo
    });

    // Aqui você pode logar o erro para um serviço de monitoramento
    // como Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // logErrorToService(error, errorInfo);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  public render() {
    if (this.state.hasError) {
      // Fallback personalizado se fornecido
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de erro padrão
      return (
        <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            
            {/* Ícone de erro */}
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
            </div>

            {/* Título */}
            <h1 className="text-2xl font-ageo-bold text-foreground dark:text-gray-100 mb-2">
              Ops! Algo deu errado
            </h1>

            {/* Descrição */}
            <p className="text-gray-600 dark:text-gray-400 font-ageo-regular mb-6">
              Ocorreu um erro inesperado na aplicação. Nossa equipe foi notificada e está trabalhando para resolver.
            </p>

            {/* Botões de ação */}
            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full flex items-center justify-center gap-2 bg-primary dark:bg-blue-600 text-white py-3 px-4 rounded-lg font-ageo-regular hover:bg-primary/90 dark:hover:bg-blue-500 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar Novamente
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 py-2 px-4 rounded-lg font-ageo-regular hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <Home className="w-4 h-4" />
                  Ir ao Início
                </button>

                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 py-2 px-4 rounded-lg font-ageo-regular hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Recarregar
                </button>
              </div>
            </div>

            {/* Detalhes do erro (apenas em desenvolvimento) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 font-ageo-regular flex items-center gap-2 hover:text-gray-700 dark:hover:text-gray-300">
                  <Bug className="w-4 h-4" />
                  Detalhes técnicos (desenvolvimento)
                </summary>
                <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-xs font-mono text-red-600 dark:text-red-400 whitespace-pre-wrap break-all">
                    <strong>Erro:</strong><br />
                    {this.state.error.toString()}
                    <br /><br />
                    <strong>Stack Trace:</strong><br />
                    {this.state.error.stack}
                    {this.state.errorInfo && (
                      <>
                        <br /><br />
                        <strong>Component Stack:</strong><br />
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </div>
                </div>
              </details>
            )}

            {/* Informações de contato */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-ageo-regular">
                Se o problema persistir, entre em contato com o suporte técnico da ACOR Engenharia.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Componente funcional para casos mais simples
export function SimpleErrorFallback({ 
  error, 
  resetError 
}: { 
  error: Error; 
  resetError: () => void;
}) {
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 my-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-ageo-bold text-red-800 dark:text-red-300 text-sm mb-2">
            Erro no componente
          </h3>
          <p className="text-red-700 dark:text-red-400 text-sm font-ageo-regular mb-3">
            {error.message}
          </p>
          <button
            onClick={resetError}
            className="text-sm bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-3 py-1 rounded font-ageo-regular hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook para usar com react-error-boundary
export function useErrorHandler() {
  return (error: Error, errorInfo: ErrorInfo) => {
    console.error('Erro capturado:', error, errorInfo);
    
    // Log para serviço de monitoramento em produção
    if (process.env.NODE_ENV === 'production') {
      // logErrorToService(error, errorInfo);
    }
  };
}

export default ErrorBoundary;